import { useEffect, useState } from 'react'

/**
 * Reactive `window.matchMedia` wrapper.
 *
 * Returns `true` when the document matches `query`, updating in real time
 * as the viewport changes (e.g. window resize, device rotation, DevTools
 * device emulation). SSR-safe — returns `false` outside the browser.
 *
 * @example
 * const isPhone = useMediaQuery('(max-width: 480px)')
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== 'undefined' && window.matchMedia(query).matches

  const [matches, setMatches] = useState<boolean>(getMatch)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mql = window.matchMedia(query)
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches)

    // Sync once on mount in case the query changed between render and effect.
    setMatches(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [query])

  return matches
}

/** Convenience: matches the project's phone breakpoint (480px). */
export function useIsPhone(): boolean {
  return useMediaQuery('(max-width: 480px)')
}

/**
 * Convenience: matches the project's mobile shell breakpoint (767px).
 *
 * MOBILE_BP — keep in sync with the `@media (max-width: 767px)` CSS tier.
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}
